// src/utils/coursesClient.js
import axios from "axios";

/**
 * Universal request function to communicate with unclekam-courses-backend.
 * Sends requests through the Netlify courses-proxy function.
 * In local dev (localhost) where Netlify functions may not be running,
 * automatically falls back to the direct local courses backend on port 9000.
 */
export async function coursesRequest({
  path,
  method = "GET",
  data = null,
  params = null,
  token = null,
  qa_mode = false,
  proxyUrl = null,
}) {
  let cleanPath = String(path || "").trim();
  if (!cleanPath.startsWith("/")) cleanPath = "/" + cleanPath;

  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  // In local dev without an explicit proxyUrl, connect directly to local backend on port 9000
  if (isLocalhost && !proxyUrl && !window.__UK_COURSES_PROXY_URL__) {
    const localApiUrl =
      import.meta.env.VITE_COURSES_API_URL || "http://localhost:9000/api/v1";
    const directResp = await axios({
      method: String(method).toUpperCase(),
      url: `${localApiUrl}${cleanPath}`,
      data: method !== "GET" ? data : undefined,
      params: params || undefined,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return directResp.data;
  }

  const defaultProxy = "/.netlify/functions/courses-proxy";
  const PROXY_URL = proxyUrl || window.__UK_COURSES_PROXY_URL__ || defaultProxy;

  const payload = {
    path: cleanPath,
    method: String(method).toUpperCase(),
    qa_mode: Boolean(qa_mode),
    ...(data ? { data } : {}),
    ...(params ? { params } : {}),
    ...(token ? { token } : {}),
  };

  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  try {
    const response = await axios.post(PROXY_URL, payload, { headers });
    return response.data;
  } catch (err) {
    const isLocalhost =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1");

    // Local dev fallback: if Netlify proxy is not running locally (404/ERR_CONNECTION_REFUSED),
    // connect directly to courses backend on port 9000.
    if (isLocalhost && (err.response?.status === 404 || !err.response)) {
      try {
        const localApiUrl =
          import.meta.env.VITE_COURSES_API_URL || "http://localhost:9000/api/v1";
        const directResp = await axios({
          method,
          url: `${localApiUrl}${cleanPath}`,
          data: method !== "GET" ? data : undefined,
          params: params || undefined,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        return directResp.data;
      } catch (localErr) {
        throw localErr;
      }
    }
    throw err;
  }
}

/**
 * Fetch an offer by embed_code, checkout_slug, or UUID
 */
export async function fetchOffer(ref, options = {}) {
  const result = await coursesRequest({
    path: `/offers/${encodeURIComponent(ref)}`,
    method: "GET",
    ...options,
  });
  return result?.data || result;
}

/**
 * Validate coupon code against an offer
 */
export async function validateCoupon({ code, offerId }, options = {}) {
  const result = await coursesRequest({
    path: "/coupons/validate",
    method: "GET",
    params: { code, offer_id: offerId },
    ...options,
  });
  return result?.data || result;
}

/**
 * Create guest checkout intent (provisions account + returns client_secret)
 */
export async function createGuestIntent(data, options = {}) {
  const result = await coursesRequest({
    path: "/checkout/guest/intent",
    method: "POST",
    data,
    ...options,
  });
  return result?.data || result;
}

/**
 * Confirm guest payment & trigger backend course/ebook fulfillment
 */
export async function confirmGuestCheckout(data, options = {}) {
  const result = await coursesRequest({
    path: "/checkout/guest/confirm",
    method: "POST",
    data,
    ...options,
  });
  return result?.data || result;
}

/**
 * Authenticate existing buyer account
 */
export async function loginBuyer(credentials, options = {}) {
  const result = await coursesRequest({
    path: "/auth/login",
    method: "POST",
    data: credentials,
    ...options,
  });
  return result?.data || result;
}

/**
 * Authenticated checkout intent
 */
export async function createAuthIntent(data, token, options = {}) {
  const result = await coursesRequest({
    path: "/checkout/intent",
    method: "POST",
    data,
    token,
    ...options,
  });
  return result?.data || result;
}

/**
 * Authenticated checkout confirm
 */
export async function confirmAuthCheckout(data, token, options = {}) {
  const result = await coursesRequest({
    path: "/checkout/confirm",
    method: "POST",
    data,
    token,
    ...options,
  });
  return result?.data || result;
}

/**
 * Charge saved card or complete payment plan subscription phase 2
 */
export async function chargeSavedCard(data, token, options = {}) {
  const result = await coursesRequest({
    path: "/checkout/charge-saved-card",
    method: "POST",
    data,
    token,
    ...options,
  });
  return result?.data || result;
}
